import React, { useState, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { createTaskAtom } from '../../../../atoms/v0/taskAtoms';
import { projectsAtom } from '../../../../atoms/v0/projectAtoms';
import { addRecentProjectIdAtom } from '../../../../atoms/v3/recentProjectsAtom';
import { Project } from '../../../../types/project';
import { addDays, startOfDay, endOfDay, format } from 'date-fns';
import { trackEvent } from '../../../../lib/analytics';
import { trackTaskEvents } from '../../../../lib/analyticsEnhanced';
import { ProjectSelector } from './ProjectSelector';

interface TaskQuickAddProps {
  projectId?: string | null;
  isTimeGroup?: boolean;
  timeGroupDate?: Date | null;
  onClose: () => void;
}

/**
 * 任务快速添加组件
 * iOS风格的简洁表单，用于快速创建任务
 */
export const TaskQuickAdd: React.FC<TaskQuickAddProps> = ({
  projectId = null,
  isTimeGroup = false,
  timeGroupDate = null,
  onClose
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);
  const [, createTask] = useAtom(createTaskAtom);
  const [projects] = useAtom(projectsAtom);
  const [, addRecentProjectId] = useAtom(addRecentProjectIdAtom);
  const inputRef = useRef<HTMLInputElement>(null);

  // 表单状态
  const [title, setTitle] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(projectId);
  const [selectedStartAt, setSelectedStartAt] = useState<string | undefined>(
    timeGroupDate ? timeGroupDate.toISOString() : undefined
  );

  // 格式化显示的日期
  const formattedSelectedStartAt = selectedStartAt
    ? format(new Date(selectedStartAt), 'MM/dd')
    : undefined;

  // 聚焦输入框
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // 处理提交
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    // 记录提交的数据，便于调试
    console.log('[TaskQuickAdd] Submitting task:', {
      title: trimmedTitle,
      projectId: selectedProjectId,
      startAt: selectedStartAt
    });

    try {
      const success = await createTask({
        title: trimmedTitle,
        projectId: selectedProjectId, // 直接传递selectedProjectId，不要使用||运算符
        startAt: selectedStartAt
      });

      if (success) {
        // 创建成功后清空输入框
        setTitle('');

        // 记录任务创建事件（使用增强版）
        trackTaskEvents.taskCreated({
          title: trimmedTitle,
          projectId: selectedProjectId,
          startAt: selectedStartAt,
          from_quick_add: true
        });

        // 如果有项目ID，添加到最近使用列表
        if (selectedProjectId) {
          addRecentProjectId(selectedProjectId);
        }

        // 任务添加成功后自动关闭快速添加界面
        onClose();
      } else {
        // 创建失败，保留用户输入，显示错误提示
        console.error('Task creation failed, please try again');
        // 可以在这里添加 toast 提示用户重试
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      // 创建失败，保留用户输入，让用户可以重试
    }
  };

  // 处理项目选择
  const handleProjectSelect = (id: string | null) => {
    console.log('[TaskQuickAdd] Project selected:', id);
    setSelectedProjectId(id);
  };

  // 处理日期选择
  const handleDateSelect = (date: Date | null) => {
    setSelectedStartAt(date ? date.toISOString() : undefined);
  };

  // 处理今天按钮
  const handleTodayClick = () => {
    const today = startOfDay(new Date());
    setSelectedStartAt(today.toISOString());
  };

  // 处理明天按钮
  const handleTomorrowClick = () => {
    const tomorrow = startOfDay(addDays(new Date(), 1));
    setSelectedStartAt(tomorrow.toISOString());
  };

  // 处理清除日期按钮
  const handleClearDate = () => {
    setSelectedStartAt(undefined);
  };

  return (
    <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg overflow-hidden shadow-ios dark:shadow-ios-dark px-4 py-3">
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* 任务标题输入框 */}
        <div className="flex items-center">
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('v3/tasks:quickAdd.placeholder')}
            className="flex-grow text-ios-body bg-transparent border-none focus:outline-none focus:ring-0 placeholder-ios-gray3 dark:placeholder-ios-dark-gray3"
          />
          <button
            type="submit"
            disabled={!title.trim()}
            className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${
              title.trim()
                ? 'bg-ios-blue dark:bg-ios-dark-blue text-white'
                : 'bg-ios-gray5 dark:bg-ios-dark-gray4 text-ios-gray3 dark:text-ios-dark-gray3'
            }`}
          >
            {t('common:actions.add')}
          </button>
        </div>

        {/* 快速选项 */}
        <div className="flex flex-wrap gap-2">
          {/* 如果是时间组，显示项目选择器 */}
          {isTimeGroup && (
            <ProjectSelector
              selectedProjectId={selectedProjectId}
              onSelectProject={handleProjectSelect}
            />
          )}

          {/* 如果是项目组，显示日期选择器 */}
          {!isTimeGroup && (
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={handleTodayClick}
                className={`px-2 py-1 text-xs rounded-full ${
                  selectedStartAt && format(new Date(selectedStartAt), 'yyyy-MM-dd') === format(startOfDay(new Date()), 'yyyy-MM-dd')
                    ? 'bg-ios-blue dark:bg-ios-dark-blue text-white'
                    : 'bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-blue dark:text-ios-dark-blue'
                }`}
              >
                {t('v3/tasks:quickAdd.today')}
              </button>
              <button
                type="button"
                onClick={handleTomorrowClick}
                className={`px-2 py-1 text-xs rounded-full ${
                  selectedStartAt && format(new Date(selectedStartAt), 'yyyy-MM-dd') === format(startOfDay(addDays(new Date(), 1)), 'yyyy-MM-dd')
                    ? 'bg-ios-blue dark:bg-ios-dark-blue text-white'
                    : 'bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-blue dark:text-ios-dark-blue'
                }`}
              >
                {t('v3/tasks:quickAdd.tomorrow')}
              </button>
              {selectedStartAt && (
                <button
                  type="button"
                  onClick={handleClearDate}
                  className="px-2 py-1 text-xs rounded-full bg-ios-gray6 dark:bg-ios-dark-gray6 text-ios-red dark:text-ios-dark-red"
                >
                  {t('common:actions.clear')}
                </button>
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};
